import { useCallback, useContext, useEffect, useState } from 'react';
import _ from 'underscore';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import {
  usePreference,
  preferenceNames,
} from '@bingads-webui-campaign-react/pmax-lite-data';
import { getUrlWithProtocol } from '@bingads-webui-campaign-react/url-utils';
import { useWizardStepPreference } from '@bingads-webui-campaign-react/pmax-lite-wizard-hooks';
import { PMAX_LITE_WIZARD_STEP_PATHS } from '@bingads-webui-campaign-react/pmax-lite-wizard-utils';

import { useGetDataFunc } from './use-get-data-function';
import { isBusinessInfoValid } from './utils';

export const useCollectBusinessInfoData = (activity) => {
  const serverContext = useContext(ServerContext);
  const {
    CurrentAccount,
  } = serverContext || {};
  const { scope } = useContext(ScopeContext);

  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState({
    isLoadingCountryList: false,
    isLoadingUserContactInfo: false,
    isLoadingCustomerContactInfo: false,
  });
  const [isInitialized, setIsInitialized] = useState({
    isInitializedCountryList: false,
    isInitializedUserContactInfo: false,
    isInitializedCustomerContactInfo: false,
  });
  const [isSaving, setIsSaving] = useState(false);
  const [isDeletingCampaign, setIsDeletingCampaign] = useState(false);
  const [businessInfoInitialized, setBusinessInfoInitialized] = useState(false);

  const [{ value: countryPreference }, { update: updateCountryPreference }]
    = usePreference(preferenceNames.SelectedCountry);
  const [{ value: websiteUrl }, { update: updateWebsiteUrl }]
    = usePreference(preferenceNames.WebsiteUrl);
  const [{ value: businessInfoPrefillEnabled }, { update: updateBusinessInfoPrefillEnabled }]
    = usePreference(preferenceNames.BusinessInfoPrefillEnabled);
  const [{ value: businessInfoStepTAndC }, { update: updateBusinessInfoStepTAndCPreference }]
    = usePreference(preferenceNames.BusinessInfoStepTAndC);
  const [, { update: updateLanguagePreference }] = usePreference(preferenceNames.SelectedLanguage);
  const [{ value: currencyPreference }] = usePreference(preferenceNames.SelectedCurrency);

  const [, updateStepPreference] = useWizardStepPreference();

  const {
    bizInfoData,
    updateBizInfoData,
    getPreferredLanguage,
    createUserFunc,
    deleteCampaignPromise,
    updateCampaignPromise,
    getSupportedCountryListPromise,
    loadCustomerContactInfoPromise,
    loadUserContactInfoPromise,
    loadBISuggestionsAndData,
    saveAccountCustomerUserData,
  } = useGetDataFunc({
    countryPreference,
    businessInfoPrefillEnabled,
    activity,
  });

  useEffect(() => {
    if (CurrentAccount && _.isEmpty(bizInfoData.supportedCountryList)
      && !isLoadingData.isLoadingCountryList
      && !isInitialized.isInitializedCountryList) {
      setIsLoadingData(prevState => ({
        ...prevState,
        isLoadingCountryList: true,
      }));
      getSupportedCountryListPromise()
        .finally(() => {
          setIsInitialized(prevState => ({
            ...prevState,
            isInitializedCountryList: true,
          }));
          setIsLoadingData(prevState => ({
            ...prevState,
            isLoadingCountryList: false,
          }));
        });
    }
  }, [CurrentAccount, bizInfoData.supportedCountryList, getSupportedCountryListPromise,
    isInitialized.isInitializedCountryList, isLoadingData.isLoadingCountryList]);

  useEffect(() => {
    if (CurrentAccount && !bizInfoData.userEmail
      && isInitialized.isInitializedCountryList
      && !isLoadingData.isLoadingUserContactInfo
      && !isInitialized.isInitializedUserContactInfo) {
      setIsLoadingData(prevState => ({
        ...prevState,
        isLoadingUserContactInfo: true,
      }));
      loadUserContactInfoPromise()
        .finally(() => {
          setIsInitialized(prevState => ({
            ...prevState,
            isInitializedUserContactInfo: true,
          }));
          setIsLoadingData(prevState => ({
            ...prevState,
            isLoadingUserContactInfo: false,
          }));
        });
      updateStepPreference({
        stepId: PMAX_LITE_WIZARD_STEP_PATHS.COLLECT_BUSINESS_INFO,
        flowFactors: { isCollectBusinessInfo: true },
      });
    }
  }, [CurrentAccount, bizInfoData.userEmail, isInitialized.isInitializedCountryList,
    isInitialized.isInitializedUserContactInfo, isLoadingData.isLoadingUserContactInfo,
    loadUserContactInfoPromise, updateStepPreference]);

  useEffect(() => {
    if (CurrentAccount && !bizInfoData.customerId
      && !isLoadingData.isLoadingCustomerContactInfo
      && !isInitialized.isInitializedCustomerContactInfo) {
      setIsLoadingData(prevState => ({
        ...prevState,
        isLoadingCustomerContactInfo: true,
      }));
      loadCustomerContactInfoPromise()
        .finally(() => {
          setIsInitialized(prevState => ({
            ...prevState,
            isInitializedCustomerContactInfo: true,
          }));
          setIsLoadingData(prevState => ({
            ...prevState,
            isLoadingCustomerContactInfo: false,
          }));
        });
    }
  }, [CurrentAccount, bizInfoData.customerId, isInitialized.isInitializedCustomerContactInfo,
    isLoadingData.isLoadingCustomerContactInfo, loadCustomerContactInfoPromise]);

  // need to resume the business info when user comes back to this step, or user abort the website change
  useEffect(() => {
    if (CurrentAccount && !isSaving && websiteUrl
      && isInitialized.isInitializedCountryList
      && isInitialized.isInitializedUserContactInfo
      && isInitialized.isInitializedCustomerContactInfo
      && ((!businessInfoInitialized && businessInfoPrefillEnabled && !bizInfoData.loadComplete)
        || bizInfoData.triggerResumeBizInfo)) {
      setBusinessInfoInitialized(true);
      const {
        userEmail,
        userPhoneNumber,
        userCallingCode,
        userCallingCountryCode,
        customerBusinessName,
        allowContact,
        allowContactByPhone,
        supportedCountryList,
      } = bizInfoData || {};

      // callingCountryCode falls back to countryPreference, and callingCode must fall back in
      // lockstep. If the user's calling code is missing, source BOTH values from the same
      // supported-country entry so callingCode is never left empty — an empty callingCode beside
      // a populated country makes isBusinessInfoValid fail and silently disables the "Next" button.
      let resolvedCallingCountryCode = userCallingCountryCode || countryPreference;
      let resolvedCallingCode = userCallingCode;
      if (!resolvedCallingCode) {
        const fallbackCountry = _.findWhere(supportedCountryList, { Code: resolvedCallingCountryCode })
          || _.first(supportedCountryList);
        resolvedCallingCountryCode = _.get(fallbackCountry, 'Code', resolvedCallingCountryCode);
        resolvedCallingCode = _.get(fallbackCountry, 'CountryCallingCode', '');
      }

      updateBizInfoData({
        email: userEmail,
        phoneNumber: userPhoneNumber,
        callingCode: resolvedCallingCode,
        callingCountryCode: resolvedCallingCountryCode,
        countryCode: countryPreference,
        websiteUrl,
        businessName: customerBusinessName,
        triggerResumeBizInfo: false,
        loadComplete: true,
        noNeedLoadSuggesion: true,
        terms: {
          accepted: businessInfoStepTAndC,
          shouldDisableChecked: businessInfoStepTAndC,
          communication: {
            AllowContact: allowContact,
            AllowContactByPhone: allowContactByPhone,
          },
        },
      });
    }
  }, [CurrentAccount, bizInfoData, businessInfoInitialized, businessInfoPrefillEnabled,
    businessInfoStepTAndC, countryPreference, isInitialized.isInitializedCountryList,
    isInitialized.isInitializedCustomerContactInfo, isInitialized.isInitializedUserContactInfo,
    isSaving, updateBizInfoData, websiteUrl]);

  useEffect(() => {
    if (bizInfoData.countryCode) {
      const preferLanguage = getPreferredLanguage(bizInfoData.countryCode);
      if (bizInfoData.language !== preferLanguage) {
        updateBizInfoData({
          language: preferLanguage,
        });
      }
    }
  }, [bizInfoData.countryCode, bizInfoData.language, getPreferredLanguage, updateBizInfoData]);

  useEffect(() => {
    const { campaignId, assetGroupId } = scope.values;
    if (campaignId && assetGroupId && bizInfoData.triggerDeleteCampaign
      && !isDeletingCampaign) {
      setIsDeletingCampaign(true);
      deleteCampaignPromise()
        .finally(() => {
          updateBizInfoData({
            triggerDeleteCampaign: false,
          });
          setIsDeletingCampaign(false);
        });
    }
  }, [bizInfoData.triggerDeleteCampaign, scope,
    deleteCampaignPromise, isDeletingCampaign, updateBizInfoData]);

  useEffect(() => {
    // Business info is considered ready once we have resumed/prefilled it (businessInfoInitialized)
    // OR once the BI suggestions load has completed (loadComplete). Relying on businessInfoInitialized
    // alone causes a stuck loading state: if a BI suggestions load finishes (setting loadComplete=true)
    // before the init promises resolve, the resume effect's `!loadComplete` guard skips setting
    // businessInfoInitialized, leaving isLoading stuck at true forever.
    const businessInfoReady = businessInfoInitialized || bizInfoData.loadComplete;
    if (!isInitialized.isInitializedCountryList
      || !isInitialized.isInitializedCustomerContactInfo
      || !isInitialized.isInitializedUserContactInfo
      || !businessInfoReady
      || isDeletingCampaign) {
      // when user first enter the page, no need to show loading,
      // because we are waiting user to input website url, we can load all the data in background.
      // but when user resume or go back, we need to load all the data again and show loading
      if (businessInfoPrefillEnabled) {
        setIsLoading(true);
      }
    } else {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    businessInfoPrefillEnabled, isDeletingCampaign, businessInfoInitialized, bizInfoData.loadComplete,
    isInitialized.isInitializedCountryList,
    isInitialized.isInitializedCustomerContactInfo, isInitialized.isInitializedUserContactInfo,
  ]);

  const save = useCallback(() => {
    // @aiContributed-MAPFuse-2026-03-12
    const {
      email,
      phoneNumber,
      callingCode,
      callingCountryCode,
      countryCode,
      language,
      websiteUrl: url,
      businessName: name,
      terms = {},
    } = bizInfoData || {};

    if (!isBusinessInfoValid(bizInfoData)) {
      return Promise.reject();
    }

    setIsSaving(true);

    return createUserFunc()
      .then(() => {
        updateCountryPreference(countryCode);
        updateWebsiteUrl(getUrlWithProtocol(url));
        updateBusinessInfoStepTAndCPreference(true);
        updateBusinessInfoPrefillEnabled(true);
        updateLanguagePreference(language);

        activity.trace(`save business info with: ${JSON.stringify(bizInfoData)}`);

        return Promise.all([
          saveAccountCustomerUserData({
            phoneNumber,
            callingCountryCode,
            callingCode,
            businessName: name,
            email,
            terms,
          }),
          updateCampaignPromise({
            currencyPreference,
            country: countryCode,
            language,
          }),
        ]);
      })
      .catch((error) => {
        activity.error(`save business info failed: ${error}`);
        return Promise.reject(error);
      })
      .finally(() => {
        setIsSaving(false);
      });
  }, [activity, bizInfoData, createUserFunc, currencyPreference,
    saveAccountCustomerUserData,
    updateBusinessInfoPrefillEnabled, updateBusinessInfoStepTAndCPreference,
    updateCampaignPromise, updateCountryPreference,
    updateLanguagePreference, updateWebsiteUrl]);

  return {
    isLoading,
    bizInfoData,
    updateBizInfoData,
    loadBISuggestionsAndData,
    websiteUrlPreference: websiteUrl,
    save,
  };
};
